import { Currency, Table } from '@mairistem/composants';
import {
	type Exercice,
	getLibelleRechercheImputation,
	type Imputation,
	ImputationSearchInput,
	Section,
	type Sens,
} from '@mairistem/comptabilite';
import { formatMontant, Icon } from '@mairistem/finances-utils';
import Decimal from 'decimal.js';
import React from 'react';
import DECISION from '../../../constants/common/decision';
import STORAGE_KEY from '../../../constants/common/storage';
import type ProImputation from '../../../interfaces/common/proImputation';

interface ProImputationItemProps {
	decision: number;
	exercice: number;
	exercices: Exercice[];
	exerciceHasGestionTva: boolean;
	exerciceHasReportFonc: boolean;
	onChangeProImputation: (data: Partial<ProImputation>) => void;
	onRemoveProImputation: () => void;
	proImputation: ProImputation;
	sens: string;
}

const ProImputationItem = ({
	decision,
	exercice,
	exerciceHasGestionTva,
	exerciceHasReportFonc,
	exercices,
	onChangeProImputation,
	onRemoveProImputation,
	proImputation,
	sens,
}: ProImputationItemProps) => {
	const handleBlurProImputationMontant = (
		{ target: { name } }: React.FocusEvent<HTMLInputElement>,
		montant: number,
	) => {
		onChangeProImputation({ [name]: montant });
	};

	const resetImputation = () => {
		onChangeProImputation({
			codeAnalytique: null,
			codeArticle: null,
			codeChapitre: null,
			codeFonction: null,
			codeOperation: null,
			codeVentilation: null,
			identifiantCoImputation: null,
		});
	};

	const handleImputationChange = (imputation: Imputation) => {
		if (imputation === null) {
			resetImputation();
			return;
		}

		const newImputation = {
			codeAnalytique: imputation.analytique?.code,
			codeArticle: imputation.article?.code,
			codeChapitre: imputation.chapitre?.code,
			codeFonction: imputation.fonction?.code,
			codeOperation: imputation.operation?.code,
			codeVentilation: imputation.ventilation?.code,
			natureBudgetaire: imputation.natureBudgetaire,
			section: imputation.chapitre?.section,
		};

		// Si l'exercice selectionne existe
		const exerciceFound = exercices.find(
			(exerciceItem) => exerciceItem.anneeExercice === exercice,
		);

		if (exerciceFound) {
			onChangeProImputation({
				...newImputation,
				identifiantCoImputation: imputation.identifiant,
			});
		} else {
			onChangeProImputation(newImputation);
		}
	};

	const getDefaultImputation = () => {
		// On stock le libelle de l'imputation dans le colonne libelle chapitre
		if (!proImputation.codeArticle && !proImputation.codeChapitre) return null;

		return {
			chapitre: {
				code: proImputation.codeChapitre,
				section: proImputation.section,
			},
			libelle: getLibelleRechercheImputation(
				proImputation.codeArticle,
				proImputation.codeOperation,
				proImputation.codeFonction,
				proImputation.codeAnalytique,
				proImputation.codeVentilation,
				proImputation.natureBudgetaire,
			),
			sens,
		};
	};

	const montantsLignes = React.useMemo(
		() => ({
			engage: proImputation.lignesEngagement.reduce(
				// TODO: Faire un utilitaire pour la gestion de la TVA
				(acc, curr) =>
					acc.add(
						exerciceHasGestionTva && !curr.tvaRecup ? curr.mttc : curr.mtht,
					),
				new Decimal(0),
			),
			mandate: proImputation.lignesMouvement.reduce(
				(acc, curr) =>
					acc.add(
						exerciceHasGestionTva && !curr.tvaRecup ? curr.mttc : curr.mtht,
					),
				new Decimal(0),
			),
		}),
		[
			exerciceHasGestionTva,
			proImputation.lignesEngagement,
			proImputation.lignesMouvement,
		],
	);

	return (
		<Table.Row>
			<Table.Cell verticalAlign="middle">
				{/* @ts-expect-error */}
				<ImputationSearchInput
					annee={exercice}
					dataTestid={`imputationSearchInputProImputation${proImputation.identifiant}`}
					defaultValue={getDefaultImputation()}
					disabled={String(exercice).length !== 4}
					onChange={handleImputationChange}
					identifiantEntite={Number(
						sessionStorage.getItem(STORAGE_KEY.ID_ENTITE_CPTA),
					)}
					placeholder={
						String(exercice).length === 4
							? 'Commencez à saisir un article'
							: 'Veuillez sélectionner un exercice'
					}
					section={
						decision === DECISION.AP
							? Section.INVESTISSEMENT
							: Section.FONCTIONNEMENT
					}
					sens={sens as Sens}
					searchComplement={{
						withAnalytique: true,
						withArticle: true,
						withChapitre: true,
						withFonction: true,
						withOperation: true,
						withVentilation: true,
					}}
				/>
			</Table.Cell>
			<Table.Cell>
				<Currency
					data-testid={`montantProImputationCurrency${proImputation.identifiant}`}
					fluid
					name="montant"
					onBlur={handleBlurProImputationMontant}
					value={proImputation.montant}
				/>
			</Table.Cell>
			<Table.Cell>
				<Currency
					data-testid={`revisionProImputationCurrency${proImputation.identifiant}`}
					fluid
					name="revision"
					onBlur={handleBlurProImputationMontant}
					value={proImputation.revision}
				/>
			</Table.Cell>
			<Table.Cell>
				<Currency
					data-testid={`rarNProImputationCurrency${proImputation.identifiant}`}
					fluid
					name="rarN"
					onBlur={handleBlurProImputationMontant}
					value={proImputation.rarN}
					disabled={!exerciceHasReportFonc && decision === DECISION.AE}
				/>
			</Table.Cell>
			<Table.Cell>
				<Currency
					data-testid={`rarN1ProImputationCurrency${proImputation.identifiant}`}
					fluid
					name="rarN1"
					onBlur={handleBlurProImputationMontant}
					value={proImputation.rarN1}
					disabled={!exerciceHasReportFonc && decision === DECISION.AE}
				/>
			</Table.Cell>
			<Table.Cell
				content={formatMontant(montantsLignes.engage.toNumber())}
				textAlign="right"
			/>
			<Table.Cell
				content={formatMontant(montantsLignes.mandate.toNumber())}
				textAlign="right"
			/>
			<Table.Cell textAlign="center">
				<Icon
					color="red"
					cursor="pointer"
					data-testid={`removeProImputationButton${proImputation.identifiant}`}
					iconSet="Lucide"
					name="Trash2"
					onClick={onRemoveProImputation}
					size={16}
				/>
			</Table.Cell>
		</Table.Row>
	);
};

export default ProImputationItem;
